import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Form as FormUntyped,
  TextField as TextFieldUntyped,
  Button as ButtonUntyped,
  SelectBox as SelectBoxUntyped,
} from "../../primary";
import { Modal as ModalUntyped, ActionModal as ActionModalUntyped } from "../../secondary";
import { CANCEL_ID, CANCEL_BTN, SUBMIT_ID, SAVE_BTN, SUCCESS_STATUS } from "../../../constants";
import styles from "./assign.module.scss";
import iconSuccess from "../../../static/images/icons-check-success.png";
import { PERMISSIONS } from "../../../constants/permissions";
import { useCreateCustomFormMutation } from "../../../features/custom-forms";
import type { CreateFormPayload } from "../../../features/custom-forms";

const Form = FormUntyped as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
  onSubmit: (...args: never[]) => unknown;
  requiredPermission?: string;
}>;

const TextField = TextFieldUntyped as unknown as React.ComponentType<{
  classes?: Record<string, string>;
  label?: React.ReactNode;
  type?: string;
  name?: string;
  id?: string;
  placeholder?: string;
  validator?: unknown;
  errors?: unknown;
  customError?: string;
  required?: boolean;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}>;

const Button = ButtonUntyped as unknown as React.ComponentType<{
  classes?: Record<string, string>;
  type?: string;
  id?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}>;

const SelectBox = SelectBoxUntyped as unknown as React.ComponentType<{
  id?: string;
  name?: string;
  label?: React.ReactNode;
  classes?: Record<string, string>;
  optionValue?: Array<{ key: string; value: string }> | string;
  placeholder?: string;
  defaultValue?: unknown;
  validator?: unknown;
  errors?: unknown;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  disabled?: boolean;
}>;

const Modal = ModalUntyped as unknown as React.ComponentType<{
  modalOpen: boolean;
  modalClose: () => void;
  heading?: React.ReactNode | false;
  className?: string;
  modalSize?: string;
  reset?: () => void;
  children?: React.ReactNode;
}>;

const ActionModal = ActionModalUntyped as unknown as React.ComponentType<{
  iconImg?: string;
  heading?: React.ReactNode;
  buttonText?: string;
  buttonAction?: () => void;
}>;

interface CreateFormProps {
  modalOpen: boolean;
  modalClose: () => void;
  onSuccess?: () => void;
}

export const CreateForm: React.FC<CreateFormProps> = ({ modalOpen, modalClose, onSuccess }) => {
  const [feature, setFeature] = useState("");
  const { register, handleSubmit, errors, reset, getValues } = useForm();
  const createFormMutation = useCreateCustomFormMutation();
  const createFormResult = createFormMutation.data;

  useEffect(() => {
    if (createFormResult?.status === SUCCESS_STATUS && onSuccess) {
      onSuccess();
    }
  }, [createFormResult, onSuccess]);

  const onSubmit = (data: CreateFormPayload) => {
    createFormMutation.reset();
    createFormMutation.mutate(data);
  };

  const modalRefresh = () => {
    reset();
    setFeature("");
    reset({ ...getValues(), feature: "" });
    modalClose();
    createFormMutation.reset();
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    reset();
    setFeature("");
    reset({ ...getValues(), feature: "" });
    modalClose();
    createFormMutation.reset();
  };

  const alertMsgClose = () => {
    modalClose();
    setFeature("");
    reset({ ...getValues(), feature: "" });
    createFormMutation.reset();
  };

  const getFeature = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeature(e.target.value);
    reset({ ...getValues(), subFeature: e.target.value === "NE1 WMS" ? "Characteristics" : "" });
  };

  const hideErrormsg = () => {
    createFormMutation.reset();
  };

  const subFeature: Record<string, boolean> = { "NE1 WMS": false, "Product Selector": true, Survey: true, "": true };

  return (
    <Modal
      modalOpen={modalOpen}
      modalClose={modalRefresh}
      heading={createFormResult?.status === undefined && "Create Form"}
      className={"modal_box"}
      modalSize={styles.custom_modal_size}
      reset={reset}
    >
      {createFormResult?.status === undefined && (
        <Form
          className={`pl-2 ${styles.patient_content} createForm`}
          onSubmit={handleSubmit(onSubmit)}
          requiredPermission={PERMISSIONS.ADMIN_FORM_BUILDER}
        >
          <TextField
            classes={{
              column: `col ${styles.col_spec}`,
              labelClass: `col-3 col-form-label ${styles.label_spec}`,
              inputColumn: "col-4 p-0",
              inputClassName: `${styles.basic_input} form-control`,
            }}
            label={"Form Name:"}
            type={"text"}
            name={"name"}
            id={"name"}
            placeholder={"Enter form name"}
            validator={register({ required: "Enter form name" })}
            errors={errors}
            customError={(createFormResult as { errorMessage?: string } | undefined)?.errorMessage}
            required={true}
            onKeyUp={hideErrormsg}
          />
          <SelectBox
            id={"feature"}
            name={"feature"}
            label={`Feature:`}
            classes={{
              column: `col-12 ${styles.col_spec}`,
              inputClassName: styles.basic_select,
              labelClassName: `col-3 col-form-label ${styles.label_spec}`,
              inputColumn: `col-4 p-0 ${styles.select_wrapper}`,
            }}
            optionValue={[
              { key: "NE1 WMS", value: "NE1 WMS" },
              { key: "Product Selector", value: "Product Selector" },
              { key: "Survey", value: "Survey" },
            ]}
            placeholder={"Select"}
            defaultValue={""}
            validator={register({ required: "Select Feature" })}
            errors={errors}
            onChange={getFeature}
            required={true}
          />
          <SelectBox
            id={"subFeature"}
            name={"subFeature"}
            label={`Sub-Feature:`}
            classes={{
              column: `col-12 ${styles.col_spec}`,
              inputClassName: `${styles.basic_select}`,
              labelClassName: `col-3 col-form-label ${styles.label_spec}`,
              inputColumn: `col-4 p-0 ${styles.select_wrapper}`,
            }}
            optionValue={feature === "NE1 WMS" ? [{ key: "Characteristics", value: "Characteristics" }] : ""}
            placeholder={""}
            defaultValue={""}
            validator={register()}
            errors={errors}
            disabled={subFeature[feature]}
          />
          <div className="col-sm-12">
            <div className="text-center pb-4">
              <Button classes={{ className: `btn ${styles.btn_custom} ${styles.btn_small}` }} type="submit" id={SUBMIT_ID}>
                {SAVE_BTN}
              </Button>
              <Button
                classes={{ className: `btn ${styles.btn_custom} ${styles.btn_small_cancel} handleCancel` }}
                id={CANCEL_ID}
                onClick={handleCancel}
              >
                {CANCEL_BTN}
              </Button>
            </div>
          </div>
        </Form>
      )}
      {createFormResult?.status === "success" && (
        <div className="text-center p-4">
          <ActionModal iconImg={iconSuccess} heading={createFormResult?.message} buttonText="Close" buttonAction={alertMsgClose} />
        </div>
      )}
    </Modal>
  );
};
